import {
  CameraEventAggregator,
  Viewer,
  CameraEventType,
  KeyboardEventModifier,
} from "cesium";
import { CameraEventTypeWithOptionalModifier } from "lib/cesium/controls/ControlsPreset";
import { AttachablePluginInterface } from "./CesiumWrapper";
import { CustomScreenSpaceCameraController } from "./CustomScreenSpaceController";

export default class CameraControlsPlugin implements AttachablePluginInterface {
  private _viewer: Viewer;
  private _controller: CustomScreenSpaceCameraController;

  get isMoving() {
    return this._controller.isMoving;
  }

  get isOrbiting() {
    return this._controller.isOrbiting;
  }

  get orbitPivot() {
    return this._controller.orbitPivot;
  }

  setViewer(viewer: Viewer) {
    this._viewer = viewer;
    this.initialize(viewer);
  }

  detachViewer() {
    this.destroy();
    this._viewer = null;
  }

  enableControls() {
    this._viewer.scene.screenSpaceCameraController.enableInputs = true;
  }

  disableControls() {
    this._viewer.scene.screenSpaceCameraController.enableInputs = false;
  }

  setControls(
    mappings: Partial<
      Record<
        "lookAround" | "orbit" | "pan" | "zoom",
        CameraEventTypeWithOptionalModifier[]
      >
    >,
  ) {
    const defaultController = this._viewer.scene.screenSpaceCameraController;
    defaultController.rotateEventTypes = mappings.pan;
    defaultController.tiltEventTypes = mappings.orbit;
    defaultController.lookEventTypes = mappings.lookAround;
    defaultController.zoomEventTypes = mappings.zoom;

    this._controller.setControlsMappings(mappings);
  }

  private initialize(viewer: Viewer) {
    const cameraEventAggregator =
      viewer.scene.screenSpaceCameraController["_aggregator"];
    this._controller = new CustomScreenSpaceCameraController(
      this._viewer,
      cameraEventAggregator,
    );

    // Disable default controls
    viewer.scene.screenSpaceCameraController.enableLook = false; //look around
    viewer.scene.screenSpaceCameraController.enableTilt = false; //orbit

    // Setting this to true will prevent the camera from passing through terrain,
    // but it then prevents dolly zooming through walls of a building.
    viewer.scene.screenSpaceCameraController.enableCollisionDetection = false;

    // At some point after cesium@1.96.0, zooming with the mouse wheel
    // would move the camera a pretty large distance. This is a workaround
    // recommended here, https://community.cesium.com/t/mouse-scroll-wheel-zoom-rate-amount/6731/14
    viewer.scene.screenSpaceCameraController.inertiaZoom = 0.0;

    // Change default shift/left-drag camera control to alt/left-drag
    viewer.scene.screenSpaceCameraController.lookEventTypes = [
      {
        eventType: CameraEventType.LEFT_DRAG,
        modifier: KeyboardEventModifier.ALT,
      },
    ];
  }

  private destroy() {
    this._controller?.destroy();
    this._controller = null;
  }
}
